import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined'
import {useDispatch, useSelector} from 'react-redux'
import {actions as importCsvActions} from '../../redux/modules/importCsv'
import {RootState} from '../../redux/rootReducer'
import {Alert, Box, Button, CircularProgress, Container, Typography} from '@mui/material'
import {ModalPage} from 'nav/ModalPage'
import FileDropZone from '../../components/FileDropZone'
import FileDetails from '../../components/FileDetails'
import TableTooltip from '../../components/TableTooltip'
import styles from './styles'
import AlertMessage from '../../components/AlertMessage'

const CSV_FORMAT_COLUMNS = [
  'Patient Name',
  'Prescriber',
  'Patient DOB',
  'Device Name',
  'Serial Number',
]

const CSV_FORMAT_ROWS = [
  ['Day, Jennifer A', 'Vraney, Robert T.', '1972-08-15', '74011', 'AG25020271'],
  ['Perez, Debra', 'Shi, Chen', '1975-03-17', '73947', 'AG25020289'],
  ['Smith, Carl', 'Marsh, Robert Anthony', '1970-03-21', '73434', 'AG25020272'],
]

const SAMPLE_CSV_PATH = `${process.env.PUBLIC_URL ?? ''}/assets/import_csv_sample.csv`

const ImportCsvPage: ModalPage = ({onClickCancel}) => {
  const [selectedFile, setSelectedFile] = useState<File | undefined>()
  const [loadingStep, setLoadingStep] = useState<string | undefined>()
  const dispatch = useDispatch()
  const {uploading, error: errorMessage, successMessage} = useSelector(
    ({importCsv}: RootState) => importCsv,
  )
  const previousUploadingRef = useRef(uploading)

  const resetFeedback = useCallback(() => {
    dispatch(importCsvActions.reset())
  }, [dispatch])

  const isCsvFile = useCallback((file: File) => {
    const fileName = file.name?.toLowerCase() ?? ''
    return (
      fileName.endsWith('.csv') ||
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel'
    )
  }, [])

  const applySelectedFile = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) {
        return
      }
      const file = fileList[0]
      if (!isCsvFile(file)) {
        setSelectedFile(undefined)
        resetFeedback()
        dispatch(
          importCsvActions.setError({
            error: {message: 'Please select a CSV file (*.csv).', details: []},
          }),
        )
        setLoadingStep(undefined)
        return
      }
      resetFeedback()
      setSelectedFile(file)
      setLoadingStep(undefined)
    },
    [dispatch, isCsvFile, resetFeedback],
  )

  const handleFilesSelected = useCallback(
    (files: FileList) => {
      if (uploading) {
        return
      }
      applySelectedFile(files)
    },
    [applySelectedFile, uploading],
  )

  const handleClearSelection = useCallback(() => {
    setSelectedFile(undefined)
    resetFeedback()
    setLoadingStep(undefined)
  }, [resetFeedback])

  const handleCancel = useCallback(() => {
    handleClearSelection()
    onClickCancel()
  }, [handleClearSelection, onClickCancel])

  const formatFileSize = useCallback((bytes: number) => {
    if (bytes === 0) {
      return '0 B'
    }
    const units = ['B', 'KB', 'MB', 'GB']
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
    const value = bytes / Math.pow(1024, unitIndex)
    const precision = value >= 10 || unitIndex === 0 ? 0 : 1
    return `${value.toFixed(precision)} ${units[unitIndex]}`
  }, [])

  const handleUpload = useCallback(async () => {
    if (!selectedFile || uploading) {
      return
    }
    const file = selectedFile
    resetFeedback()
    setLoadingStep('Uploading file...')
    try {
      await new Promise<void>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve()
        reader.onerror = () => reject(new Error('Unable to read the CSV file. Please try again.'))
        reader.readAsText(file)
      })
      setLoadingStep('Processing file...')
      dispatch(importCsvActions.upload({file}))
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : 'Upload failed.'
      dispatch(importCsvActions.setError({error: {message: message, details: []}}))
      setLoadingStep(undefined)
      setSelectedFile(undefined)
    }
  }, [dispatch, resetFeedback, selectedFile, uploading])

  useEffect(() => {
    if (previousUploadingRef.current && !uploading && loadingStep) {
      setLoadingStep(undefined)
      setSelectedFile(undefined)
    }
    previousUploadingRef.current = uploading
  }, [loadingStep, uploading])

  useEffect(() => {
    return () => {
      resetFeedback()
      setSelectedFile(undefined)
      setLoadingStep(undefined)
    }
  }, [resetFeedback])

  const fileDetails = useMemo(() => {
    if (!selectedFile) {
      return null
    }
    return (
      <FileDetails
        name={selectedFile.name}
        sizeLabel={formatFileSize(selectedFile.size)}
        helperText="The file will be validated once uploaded."
        onRemove={handleClearSelection}
        disabled={uploading}
      />
    )
  }, [formatFileSize, handleClearSelection, selectedFile, uploading])

  const titleElement = (
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
      <Typography variant="subtitle1">CSV Format Example</Typography>
      <Button
        variant="text"
        size="small"
        href={SAMPLE_CSV_PATH}
        download
        sx={{
          color: 'common.white',
          boxShadow: 'none',
          '&:hover': {bgcolor: 'grey.500', boxShadow: 'none'},
        }}>
        Download Sample CSV
      </Button>
    </Box>
  )

  return (
    <Container component="main">
      <Box sx={styles.modal}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, marginBottom: 3}}>
          <Typography variant="h4" sx={{...styles.headerLabel, marginBottom: 0}}>
            Import CSV
          </Typography>
          <TableTooltip
            columns={CSV_FORMAT_COLUMNS}
            rows={CSV_FORMAT_ROWS}
            title={titleElement}
            icon={<HelpOutlinedIcon fontSize="medium" />}
            iconButtonProps={{'aria-label': 'CSV format example'}}
          />
        </Box>

        <Typography variant="body1" sx={{marginBottom: 3}}>
          Upload a CSV file with patients and devices data. You can drag and drop a file or browse
          your computer to select one manually.
        </Typography>

        <AlertMessage error={errorMessage} severity="error" sx={styles.feedback} />

        {successMessage && (
          <Alert severity="success" sx={styles.feedback}>
            {successMessage}
          </Alert>
        )}

        {uploading && (
          <Box sx={styles.progress} role="status" aria-live="polite">
            <CircularProgress size={20} />
            <Typography variant="body2" sx={styles.progressLabel}>
              {loadingStep ?? 'Uploading file...'}
            </Typography>
          </Box>
        )}

        <FileDropZone
          fileTypeLabel="CSV"
          accept={['.csv', 'text/csv', 'application/vnd.ms-excel']}
          disabled={uploading}
          helperText="or click to choose a file from your computer"
          onFilesSelected={handleFilesSelected}
        />

        {fileDetails}

        <Box sx={styles.buttonContainer}>
          <Button sx={styles.cancelButton} color="warning" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpload} disabled={!selectedFile || uploading}>
            Upload
          </Button>
        </Box>
      </Box>
    </Container>
  )
}

export default ImportCsvPage
